import {Box, Link, Typography} from '@mui/material';
import {t} from 'i18next';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {ReactComponent as FileUpload} from '../../../../assests/svg/upload-Icon.svg';
import {useSequentialUploadQueue} from '../../../../hooks/upload-evidence-source/useSequentialUploadFiles';
import {setEvidenceSourceDetails} from '../../../../redux/slices/uploadEvidenceSourceSlice';
import {
  DragAndDropUploadStatus,
  FileCatalystModalType,
} from '../../../../types/upload-new-eviidence/evidenceSourceTypes';
import AurButton from '../../../common/aur-button/AurButton';
import AurDragAndDropBox from '../../../common/aur-drag-and-drop/AurDragAndDropBox';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import AurPopup from '../../../common/aur-popup/AurPopup';
import {EvidenceSourceInfo} from './components/EvidenceSourceInfo';
import {FileCatalystGrid} from './components/FileCatalystGrid';
import {FileCatalystPopUpModal} from './components/FileCatalystPopUpModal';
import {useAnalytics} from '../../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';
import {getFileSize} from '../../../../utils/utils';

interface UploadDragAndDropTabProps {
  onChangeUploadFileCount?: (fileCount: number) => void;
}

const UploadDragAndDropTab: React.FC<UploadDragAndDropTabProps> = ({
  onChangeUploadFileCount,
}) => {
  const [isShowLearnMoreModal, setIsShowLearnMoreModal] =
    useState<boolean>(false);
  const {trackGAEvent} = useAnalytics();
  const [fileActionModal, setFileActionModal] = useState<{
    type: FileCatalystModalType | null;
    fileId: string;
  }>({
    type: null,
    fileId: '',
  });
  const {
    uploadQueueState,
    isUploading,
    isIntakeLoading,
    canManageEvidence,
    addFiles,
    cancelUpload,
    removeFile,
    abortAllUploads,
    handleInitiateUpload,
    retryUpload,
  } = useSequentialUploadQueue();

  useEffect(() => {
    return () => {
      abortAllUploads(); // stop all uploads when leaving screen
    };
  }, [abortAllUploads]);

  useEffect(() => {
    onChangeUploadFileCount?.(uploadQueueState.length);
  }, [onChangeUploadFileCount, uploadQueueState.length]);

  const uncompletedFiles = useMemo(
    () =>
      uploadQueueState.filter(
        (item) => item.status !== DragAndDropUploadStatus.Completed
      ),
    [uploadQueueState]
  );

  const completedFiles = useMemo(
    () =>
      uploadQueueState.filter(
        (item) => item.status === DragAndDropUploadStatus.Completed
      ),
    [uploadQueueState]
  );

  const isInitiateUploadEnabled = useMemo(() => {
    const hasPending = uploadQueueState.some(
      (f) =>
        f.status === DragAndDropUploadStatus.InQueue ||
        f.status === DragAndDropUploadStatus.InProcess
    );

    return completedFiles.length > 0 && !hasPending;
  }, [uploadQueueState, completedFiles]);

  const handleSelectedFile = useCallback(
    (files: File[], isFilesDropped: boolean) => {
      const fileArray = Array.from(files);
      if (fileArray.length === 0) return;

      addFiles(fileArray);

      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: isFilesDropped
            ? t('gaTracking.uploadEvidence.dragFiles')
            : t('gaTracking.uploadEvidence.chooseFiles'),
          type: isFilesDropped ? GA_CLICK_TYPES.DRAG : GA_CLICK_TYPES.BUTTON,
          region: GA_REGIONS.TAB,
          action: t('gaTracking.uploadEvidence.uploadFileBegin'),
        },
        files: {
          file_size_mb: getFileSize(fileArray, 'MB').toString(),
        },
        subPage: {
          subpage_name: t('gaTracking.uploadEvidence.dndUpload'),
          subpage_type: GA_SUBPAGE_TYPE.TAB,
        },
      });
    },
    [addFiles]
  );

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleInitiateUploadClick = async () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.uploadEvidence.initiateIntake'),
        type: GA_CLICK_TYPES.LINK,
        region: GA_REGIONS.TAB,
        action: t('gaTracking.uploadEvidence.uploadFileComplete'),
      },
      files: {
        file_size_mb: getFileSize(
          completedFiles.map((f) => f.file),
          'MB'
        ).toString(),
        file_count: `${completedFiles.length}`,
        success_rate: `${((completedFiles.length * 100) / (completedFiles.length + uncompletedFiles.length)).toFixed(2)}`,
      },
      subPage: {
        subpage_name: t('gaTracking.uploadEvidence.dndUpload'),
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
    });

    const data = await handleInitiateUpload();

    // failed
    if (!data) return;

    // success navigation
    dispatch(setEvidenceSourceDetails(undefined));
    localStorage?.setItem('currentPath', '');
    if (canManageEvidence) navigate('/manage-evidence');
    else navigate('/welcome-single-client');
  };

  const handleRetry = useCallback(
    (id: string) => {
      retryUpload(id);
      const fileQueueItem = uncompletedFiles.find((i) => i.id === id);
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: t('gaTracking.uploadEvidence.retryUpload'),
          type: GA_CLICK_TYPES.LINK,
          region: GA_REGIONS.TAB,
          action: t('gaTracking.uploadEvidence.uploadFileRetryUploading'),
        },
        files: {
          file_size_mb: getFileSize(
            fileQueueItem ? [fileQueueItem.file] : [],
            'MB'
          ).toString(),
        },
        subPage: {
          subpage_name: t('gaTracking.uploadEvidence.dndUpload'),
          subpage_type: GA_SUBPAGE_TYPE.TAB,
        },
      });
    },
    [retryUpload]
  );

  return (
    <Box className={'drag-and-drop-container'}>
      <EvidenceSourceInfo />
      <Box className={'upload-file-container'}>
        <AurDragAndDropBox
          dataTestId="uploadBtn-test"
          limitFileSizeInBytes={2147483648} // 2GB in bytes
          acceptFileType=".zip, .zipx, .rar, application/zip, application/x-zip-compressed, application/octet-stream, application/x-zip, application/vnd.rar, application/x-rar-compressed, application/x-compressed"
          FileUploadIcon={FileUpload}
          orText={true}
          dragAndDropText={t('common.aurUploadBox.dragDropText')}
          buttonLabel={t('common.aurUploadBox.chooseFiles')}
          buttonHint={t('common.aurUploadBox.chooseFiles')}
          dragAndDropDescription={
            <Typography
              tabIndex={0}
              className="typo-text body3_size drag-and-drop-desc"
            >
              {t('fileCatalyst.httpsDragAndDropDescription')}
              <Typography
                component={'span'}
                className="typo-text body3_size drag-and-drop-2gb"
              >
                {t('fileCatalyst.2gb')}
              </Typography>
              <Typography
                component={'span'}
                tabIndex={0}
                className="typo-text body3_size drag-and-drop-desc"
              >
                {t('fileCatalyst.httpsDragAndDropDescriptionEnd')}
              </Typography>
              <Link
                href="#"
                className="typo-text body3_size link-learn-more"
                data-testid="learn-more"
                role="button"
                aria-label={`${t('fileCatalyst.learnMore')} for ${t('common.aurUploadBox.dragDropText')}`}
                onClick={(e) => {
                  e.preventDefault();
                  setIsShowLearnMoreModal(true);
                }}
              >
                {t('fileCatalyst.learnMore')}
              </Link>
            </Typography>
          }
          handleFile={handleSelectedFile}
        />
        <FileCatalystGrid
          type={'in-progress'}
          title={t('fileCatalyst.dragAndDropTransferProgress')}
          emptyMessage={t('fileCatalyst.noFilesQueued')}
          data={uncompletedFiles}
          onCancel={(id) => {
            const fileQueueItem = uncompletedFiles.find((i) => i.id === id);
            trackGAEvent({
              eventName: GA_EVENT_NAMES.CLICK,
              click: {
                name: t('gaTracking.uploadEvidence.cancelUpload'),
                type: GA_CLICK_TYPES.LINK,
                region: GA_REGIONS.TAB,
                action: t(
                  'gaTracking.uploadEvidence.uploadFileCancelUploading'
                ),
              },
              files: {
                file_size_mb: getFileSize(
                  fileQueueItem ? [fileQueueItem.file] : [],
                  'MB'
                ).toString(),
              },
              subPage: {
                subpage_name: t('gaTracking.uploadEvidence.dndUpload'),
                subpage_type: GA_SUBPAGE_TYPE.TAB,
              },
            });
            setFileActionModal({
              type: FileCatalystModalType.CancelUpload,
              fileId: id,
            });
          }}
          onRetry={handleRetry}
        />
        <FileCatalystGrid
          type={'completed'}
          title={t('fileCatalyst.uploadedFiles', {
            count: completedFiles.length,
          })}
          emptyMessage={t('fileCatalyst.noFilesUploaded')}
          data={completedFiles}
          onRemove={(id) => {
            const fileQueueItem = completedFiles.find((i) => i.id === id);
            trackGAEvent({
              eventName: GA_EVENT_NAMES.CLICK,
              click: {
                name: t('gaTracking.uploadEvidence.removeFile'),
                type: GA_CLICK_TYPES.LINK,
                region: GA_REGIONS.TAB,
                action: t(
                  'gaTracking.uploadEvidence.uploadFileRemoveUploading'
                ),
              },
              files: {
                file_size_mb: getFileSize(
                  fileQueueItem ? [fileQueueItem.file] : [],
                  'MB'
                ).toString(),
              },
              subPage: {
                subpage_name: t('gaTracking.uploadEvidence.dndUpload'),
                subpage_type: GA_SUBPAGE_TYPE.TAB,
              },
            });
            setFileActionModal({
              type: FileCatalystModalType.Remove,
              fileId: id,
            });
          }}
        />
        <Box className="file-action-btn">
          {isUploading && (
            <Box className="upload-loading-container" tabIndex={0}>
              <AurLoadingIndicator size={18} className="loading-icon" />
              <Typography className={`typo-text subhead4_size`}>
                {t('fileCatalyst.uploading')}
              </Typography>
            </Box>
          )}
          <AurButton
            type="continue"
            label={t('fileCatalyst.initiateIntake')}
            title={t('fileCatalyst.initiateIntake')}
            size="Large"
            onClick={() => handleInitiateUploadClick()}
            ariaDescription={t('fileCatalyst.initiateIntake')}
            disableHint={t('fileCatalyst.initiateIntake')}
            disabled={!isInitiateUploadEnabled}
          />
        </Box>
      </Box>
      {isShowLearnMoreModal && (
        <AurPopup
          className="aur-drag-and-drop-popup"
          showPopupTitle={true}
          showCloseIcon={true}
          showCancelOption={false}
          aria-label={t('fileCatalyst.dragAndDropModal.title')}
          closeIconHint={t('fileCatalyst.leaveScreenModal.closeHint')}
          labelActionOk={t('buttons.close')}
          open={isShowLearnMoreModal}
          contentType="element"
          popupContent={
            <Box>
              <Typography
                tabIndex={0}
                className="typo-text subhead3_size title-text"
              >
                {t('fileCatalyst.dragAndDropModal.title')}
              </Typography>
              <Typography
                tabIndex={0}
                className="typo-text body2_size description-text"
              >
                {t('fileCatalyst.dragAndDropModal.point1')}
              </Typography>
              <Typography
                tabIndex={0}
                className="typo-text body2_size description-text"
              >
                {t('fileCatalyst.dragAndDropModal.point2')}
              </Typography>
              <Typography
                tabIndex={0}
                className="typo-text body2_size description-text"
              >
                {t('fileCatalyst.dragAndDropModal.point3')}
              </Typography>
            </Box>
          }
          onOk={() => setIsShowLearnMoreModal(false)}
          onCancel={() => setIsShowLearnMoreModal(false)}
        />
      )}
      {fileActionModal.type && (
        <FileCatalystPopUpModal
          type={fileActionModal.type}
          isShow={Boolean(fileActionModal.type)}
          onClose={() => setFileActionModal({type: null, fileId: ''})}
          onYes={() => {
            if (fileActionModal.type === FileCatalystModalType.CancelUpload) {
              cancelUpload(fileActionModal.fileId);
            }
            if (fileActionModal.type === FileCatalystModalType.Remove) {
              removeFile(fileActionModal.fileId);
            }
            setFileActionModal({type: null, fileId: ''});
          }}
        />
      )}
      {<AurLoadingIndicator overlay={true} isOpen={isIntakeLoading} />}
    </Box>
  );
};

export {UploadDragAndDropTab};
